import type { CSSProperties } from 'react';
import { Arrows } from '../components/atomic/buttons/arrows/Arrows';
import { GoBackButton } from '../components/atomic/buttons/go_back_button/GoBackButton';
import '../components/atomic/layout/app_screen/app_screen.css';
import { containFitScale } from '../../../infrastructure/runtime';
import { useAppFit } from '../../hooks/useAppFit';
import { CharacterName } from '../components/atomic/character/character_names/CharacterName';
import { SkyAtmosphere } from '../components/composite/atmosphere/sky_atmosphere/SkyAtmosphere';
import { ScreenHeader } from '../components/atomic/layout/screen_header/ScreenHeader';
import { Button } from './button/Button';
import { CharacterPaginator } from './character_paginator/CharacterPaginator';
import { CharacterIcon, CHARACTER_ORDER } from './characters_icon/CharacterIcon';
import type { SelectCharacterScreenController } from './SelectCharacterScreenController';
import {
  CHARACTER_NAMES_LAYOUT,
  HORIZONTAL_ARROW_POSITIONS,
  type SelectCharacterScreenModel,
} from './SelectCharacterScreenModelScreen';
import { HeaderText } from './header_text/HeaderText';
import { StatsContent } from './stats_content/StatsContent';
import './select_character_screen.css';
import type { ScreenViewProps } from '../shared/screenViewProps';
import { useSwipeNavigate } from '../shared/gestures/useSwipeNavigate';

export type SelectCharacterScreenViewProps = ScreenViewProps<
  SelectCharacterScreenModel,
  SelectCharacterScreenController
>;

export function SelectCharacterScreenView({
  model,
  controller,
}: SelectCharacterScreenViewProps) {
  const swipe = useSwipeNavigate({
    axis: 'horizontal',
    onSwipe: controller.handleNavigate,
  });
  const fit = useAppFit();
  const contain = containFitScale(fit.scale);

  return (
    <div
      className="app-screen select-character-screen"
      style={swipe.style}
      onPointerDown={swipe.onPointerDown}
      onPointerUp={swipe.onPointerUp}
      onPointerCancel={swipe.onPointerCancel}
      onClickCapture={swipe.onClickCapture}
    >
      <SkyAtmosphere
        variant="delivery"
        sunBeamsClassName="select-character-screen__sun-beams"
      />
      <div
        className="select-character-plaques"
        style={
          {
            '--fit-contain-inv-x': String(contain.invX),
            '--fit-contain-inv-y': String(contain.invY),
            '--fit-contain-uniform': String(contain.uniform),
          } as CSSProperties
        }
      >
        <ScreenHeader />
        <HeaderText />
        <CharacterPaginator
          current={model.selectedIndex + 1}
          total={model.totalCharacters}
        />
        <CharacterIcon name={model.selectedCharacter} />
        {CHARACTER_ORDER.map((id) => (
          <CharacterName
            key={id}
            name={id}
            {...CHARACTER_NAMES_LAYOUT[id]}
            className={
              id === model.selectedCharacter
                ? undefined
                : 'select-character-name--hidden'
            }
          />
        ))}
        <StatsContent stats={model.stats} />
        <Arrows
          directions={['left', 'right']}
          positions={HORIZONTAL_ARROW_POSITIONS}
          onNavigate={controller.handleNavigate}
        />
        <GoBackButton onClick={controller.handleGoBack} />
        <Button onClick={controller.handleConfirm} />
      </div>
    </div>
  );
}
